<template>
  <div
    v-if="!item.destroy"
    class="gf_pack__wrap wrap-pack"
    :class="{ 'gf_pack__wrap--selected': selectedPack === indexItem }">
    <div v-if="error.show" class="message-errors" :style="{ 'max-width': error.maxWidth }">
      <label class="label">
        {{ error.message }}
      </label>
    </div>
    <div
      v-if="!(hideItemRow && selectedPack === indexItem)"
      class="gf_pack__wrap-row wrap-row"
      :class="{ opened: selectedPack === indexItem }"
      :data-input-focus="isInputFocus">
      <div class="left-col">
        <slot name="imageSlot"></slot>
      </div>
      <div class="center-col" :data-has-image-slot="hasImageSlot()">
        <Teleport v-if="isOpenTooltip" to="#tooltips">
          <Tooltip :pointer="true" :width="'auto'" />
        </Teleport>
        <span v-show="!isHoveringName && !isInputFocus" class="lbl-name" @mouseenter="onEnterName">
          {{ item.name }}
        </span>
        <input
          v-show="isHoveringName || isInputFocus"
          v-model.trim="item.name"
          v-popover:tooltip.top="'Click to rename'"
          :style="[error.show ? { border: '1px solid #F44336' } : true]"
          class="lbl-input"
          placeholder="New style name"
          type="text"
          :disabled="!isReadyToSave && indexItem != invalidPack"
          @mouseover="onEnterName"
          @mouseleave="onLeaveName"
          @focus="setValueBeforeInput"
          @keyup.esc="changeEnterName"
          @keyup.enter="changeEnterName"
          @change="changeEnterName"
          @input="validateItemName"
          @focusout="inputChange" />
      </div>
      <div class="right-col">
        <div v-if="isShowAction" class="wrap-absolute-action" :data-has-image-slot="hasImageSlot()">
          <div class="d-flex">
            <a
              v-if="duplicateElementValid"
              v-popover:tooltip.top="'Duplicate'"
              class="item-action copy-action"
              @click="onDuplicate"
              @mouseover="isOpenTooltip = true"
              @mouseleave="isOpenTooltip = false">
              <i class="icon icon-clone gf_pack_copy" />
            </a>
            <a
              v-if="item.isDefault"
              :key="item.id"
              v-popover:tooltip.top="'You can not delete default ' + typePack"
              class="item-action delete-action"
              disabled
              @mouseover="onHoverDelete"
              @mouseleave="onLeaveDelete">
              <i class="icon icon-delete gf_pack_delete" />
            </a>
            <a
              v-else
              :key="item.id"
              v-popover:tooltip.top="'Coming soon'"
              class="item-action delete-action"
              disabled
              @mouseover="onHoverDelete"
              @mouseleave="onLeaveDelete">
              <i class="icon icon-delete gf_pack_delete" />
            </a>
          </div>
        </div>
        <a class="link-col-action" @click="toggleAction">
          <i :class="[selectedPack === indexItem ? 'icon-chevron-up' : 'icon-chevron-down', 'icon gf_pack_expand']" />
        </a>
      </div>
    </div>
    <div v-if="selectedPack === indexItem" class="wrap-panel-settings">
      <slot name="settingSlot"></slot>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapState, mapGetters } from 'vuex';
import { presetOptionTypes } from '../../constants/styleConstant';
import type { PresetOption } from '@/types/global-style';

export default defineComponent({
  name: 'Pack',
  props: {
    item: {
      type: Object,
      default: () => {},
    },
    indexItem: {
      type: Number,
      default: -1,
    },
    selectedPack: {
      type: Number,
      default: 0,
    },
    hideItemRow: {
      type: Boolean,
      default: false,
    },
    isOnDuplicate: {
      type: Boolean,
      default: true,
    },
    isReadyToSave: {
      type: Boolean,
      default: true,
    },
    invalidPack: {
      type: Number,
      default: -1,
    },
    typePack: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    isInputFocus: false, // Nếu input đang được focus để edit thì sẽ ẩn action delete và clone đi
    error: {
      show: false,
      message: '',
      maxWidth: '',
    },
    isOpenTooltip: false,
    oldNameValue: '',
    isShowAction: true,
    isHoveringName: false,
  }),
  computed: {
    ...mapState('sidebar', {
      currentPresetOptionType: (state) => (state as any).currentPresetOptionType,
      currentPreset: (state) => (state as any).currentPreset,
    }),
    ...mapGetters('sidebar', {
      currentPresetOptions: 'getCurrentPresetOptions',
    }),
    presetFormType() {
      if (this.currentPresetOptionType === presetOptionTypes.border) {
        return this.currentPresetOptions;
      } else if (this.currentPresetOptionType === presetOptionTypes.color) {
        return this.currentPreset.presetOptions?.colors;
      } else if (this.currentPresetOptionType === presetOptionTypes.corner) {
        return this.currentPresetOptions;
      } else if (this.currentPresetOptionType === presetOptionTypes.button) {
        return this.currentPresetOptions;
      }

      return [];
    },
    elements() {
      return (this as any).currentPresetOptions.filter((element: { destroy: any }) => !element.destroy);
    },
    duplicateElementValid() {
      return this.elements.length < 10;
    },
  },
  watch: {
    currentPresetOptions() {
      this.validateItemName();
    },
  },
  created() {},
  methods: {
    setValueBeforeInput() {
      this.isInputFocus = true;
      this.oldNameValue = this.item.name;
      this.isOpenTooltip = false;
      this.isShowAction = false;
    },
    hasImageSlot() {
      return !!this.$slots.imageSlot;
    },
    changeEnterName(e: any) {
      this.$emit('onInputChange', this.indexItem, e.target.value);
      this.validateItemName();
      this.isInputFocus = false;
      e.target.blur();
    },
    inputChange(e: any) {
      let value = e.target.value;
      if (this.error.show) {
        value = this.oldNameValue;
      }
      this.item.name = value;
      this.validateItemName();
      this.$emit('onInputChange', this.indexItem, value);
      this.isInputFocus = false; // Reset trạng thái focus để khi hover vào lại show ra action
      e.target.blur();
    },
    onEnterName() {
      if (!this.isInputFocus) {
        this.isOpenTooltip = true;
      }
      this.isShowAction = false;
      this.isHoveringName = true;
    },
    onLeaveName() {
      this.isOpenTooltip = false;
      this.isShowAction = true;
      this.isHoveringName = false;
    },
    onHoverInputName() {
      if (!this.isInputFocus) {
        this.isOpenTooltip = true;
      }
      this.isShowAction = false;
      this.isHoveringName = true;
    },
    onHoverDelete() {
      this.isOpenTooltip = true;
    },
    onLeaveDelete() {
      this.isOpenTooltip = false;
    },
    validateItemName() {
      this.error.show = false;

      const existItem = (this as any).presetFormType
        .filter((element: { destroy: any }) => !element.destroy)
        .filter((preset: PresetOption) => preset.name.toLowerCase() === this.item.name.toLowerCase());

      if (existItem.length > 1) {
        this.error = {
          show: true,
          maxWidth: '272px',
          message: this.$t('sidebarGS.styleDetail.general.errorInputNameExisted'),
        };

        this.$emit('validateItemName', { isAvailable: false, invalidPack: this.indexItem });
        return false;
      }

      if (this.item.name.length < 1) {
        this.error = {
          show: true,
          maxWidth: '172px',
          message: this.$t('sidebarGS.styleDetail.general.errorInputNameBlank'),
        };

        this.$emit('validateItemName', { isAvailable: false, invalidPack: this.indexItem });
        return false;
      }

      if (this.item.name.length > 30) {
        this.error = {
          show: true,
          maxWidth: '160px',
          message: this.$t('sidebarGS.styleDetail.general.errorInputNameMax', { maxLength: 30 }),
        };

        this.$emit('validateItemName', { isAvailable: false, invalidPack: this.indexItem });
        return false;
      }

      this.$emit('validateItemName', { isAvailable: true, invalidPack: -1 });
      return true;
    },
    toggleAction(e: any) {
      if (this.selectedPack === this.indexItem) {
        this.$emit('onChangePack', -1);
      } else {
        this.$emit('onChangePack', this.indexItem);
      }

      this.$emit('toggleAction', e);
    },
    onDelete() {
      this.$emit('onDelete', this.indexItem);
    },
    onDuplicate() {
      this.$emit('onDuplicate', this.indexItem);
    },
  } as any,
});
</script>

<style scoped lang="scss">
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';
.message-errors {
  background: #676767;
  border-radius: 4px;
  height: 44px;
  max-width: 272px;
  @include flex-center;
  margin-bottom: 8px;
  margin-top: 6px;
  position: relative;

  .label {
    font-style: normal;
    font-weight: 500;
    font-size: 12px;
    line-height: 14px;
    color: #ffffff;
    margin-left: 8px;
    margin-right: 8px;
    padding-bottom: 0;
  }

  &:after {
    content: '';
    position: absolute;
    bottom: -6px;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid map-get($text-light-mode, 'low-contrast');
    clear: both;
  }
}

.wrap-pack {
  color: $highlight2;
  font-size: 12px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 6px;
  }

  .wrap-row {
    display: flex;
    cursor: pointer;
    border: 1px solid map-get($hybrid-shade-dark, '200');
    padding: 7px 4px;
    border-radius: $radius;
    -moz-border-radius: $radius;
    -webkit-border-radius: $radius;
    position: relative;
    height: 40px;

    &:hover .wrap-absolute-action {
      display: block !important;
    }

    &.opened {
      background: #494949;
      height: 40px;
      border-radius: $radius $radius 0 0;
      transition: all 0.5s;
    }

    .left-col {
      flex: 0 0 96px;
      justify-content: flex-start;
      align-items: center;
      background: #ffffff;
      display: flex;
      border-radius: $radius;
      -moz-border-radius: $radius;
      -webkit-border-radius: $radius;

      &:empty {
        display: none;
      }

      .wrap-thumb-preview {
        background: #f2e8f7;
        width: 96px;
        height: 48px;
        display: flex;
        padding: 12px 4px;
        align-items: center;
        justify-content: center;
        border-radius: 3px;
        overflow: hidden;
      }
    }

    .center-col {
      flex: 1;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      padding-left: 4px !important;
      max-width: 100%;
      overflow: hidden;
      .lbl-name {
        display: inline-block;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        padding: 4px;
      }

      .lbl-name,
      .lbl-input {
        font-size: 12px;
        background-color: transparent;
        border: 1px solid transparent;
        color: $highlight2;
        cursor: pointer;
        border-radius: $radius;
        -moz-border-radius: $radius;
        -webkit-border-radius: $radius;
        transition: border 300ms;
      }

      .lbl-input {
        margin-left: 0px !important;
        height: 24px;
        &:hover {
          border: 1px solid map-get($hybrid-shade-light, '500');
        }

        &:focus {
          cursor: text;
          border: 1px solid map-get($primary-blue, '275');
        }
      }
    }

    .right-col {
      flex: 1 1 auto;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      flex-grow: 0;
      flex-shrink: 0;

      .link-col-action {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 4px;
        height: 24px;
        width: 24px;
        border-radius: $radius;
        font-size: 16px;

        &:hover {
          background: map-get($hybrid-shade-dark, '300');
        }
      }

      .wrap-absolute-action {
        display: none;

        &[data-has-image-slot='true'] {
          top: 25px;
        }

        .item-action {
          padding: 4px;
          margin-right: 4px;
          height: 24px;
          width: 24px;
          border-radius: $radius;
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 16px;
        }

        .copy-action {
          &:hover {
            background: map-get($hybrid-shade-dark, '300');
          }
        }
      }

      .delete-action {
        color: $text-dark-disabled;
        // &:hover {
        //   background: map-get($accent-color-red, "400");
        // }
      }
    }

    .wrap-panel-settings {
      .wrap-editor-border {
        padding: 12px;
      }
    }
  }
}
</style>
